import type { WeatherOptions, WeatherSight } from "../../api/types";

/** Calque « Visibilité » (conception validée par Alexis le 31/10/2026, `_visibilite/CONCEPTION.md`), sans moteur de
 *  carte : rien n'est tracé par beau temps ; par visibilité réduite seulement (règle de l'analyse du terrain, servie
 *  par `POST /maps/{slug}/weather`, champ `sight`), un anneau par unité — bleu ami au rayon de la vue amie, rouge au
 *  rayon du tir direct ennemi —, un voile gris sur le camp ou le terrain libre et une pastille « Visibilité ». Module
 *  pur, essayé seul. */

/** Épaisseur des anneaux (trait plein, sans remplissage). */
export const RING_WIDTH = 1.25;

/** Circonférence de la Terre (m) et taille des tuiles des moteurs (512 px) : mètres par pixel au zoom z. */
const EARTH_CIRCUMFERENCE_M = 40_075_016.686;
const TILE_PX = 512;
const MAX_ZOOM = 24;

export interface VisibilityState {
  /** Visibilité réduite : anneaux, voile et pastille tracés ; faux par beau temps (rien tracé). */
  reduced: boolean;
  friendlyKm: number | null;
  enemyKm: number | null;
  /** Visibilité du jour (km), celle de l'aperçu Météo ; null sans météo calculable. */
  range: [number, number] | null;
}

/** État du calque pour la météo appliquée du dossier affiché ; `null` tant que la météo n'est pas connue. */
export function visibilityState(options: Pick<WeatherOptions, "preview" | "sight"> | undefined | null): VisibilityState | null {
  if (!options) return null;
  const sight: WeatherSight | null | undefined = options.sight;
  const reduced = Boolean(sight?.reduced && sight.friendly_km && sight.enemy_km);
  return {
    reduced,
    friendlyKm: reduced ? sight!.friendly_km : null,
    enemyKm: reduced ? sight!.enemy_km : null,
    range: options.preview?.visibility_km ?? null,
  };
}

/** Rayon (px) d'un cercle de `metres` à la latitude `lat` au zoom 0 ; il double à chaque niveau de zoom. */
export function radiusAtZoom0(metres: number, lat: number): number {
  return (metres * TILE_PX) / (EARTH_CIRCUMFERENCE_M * Math.max(0.05, Math.cos((lat * Math.PI) / 180)));
}

/** `circle-radius` des anneaux : rayon métrique (vue amie pour un ami, tir direct pour un ennemi), interpolé de façon
 *  exponentielle sur le zoom (base 2), le même dans les deux moteurs de carte. */
export function ringRadius(friendlyKm: number, enemyKm: number, lat: number): unknown[] {
  const at = (zoom: number) => [
    "match",
    ["get", "side"],
    "ENI",
    radiusAtZoom0(enemyKm * 1000, lat) * 2 ** zoom,
    radiusAtZoom0(friendlyKm * 1000, lat) * 2 ** zoom,
  ];
  return ["interpolate", ["exponential", 2], ["zoom"], 0, at(0), MAX_ZOOM, at(MAX_ZOOM)];
}

/** Latitude de référence des anneaux : le milieu de l'emprise [ouest, sud, est, nord] du camp ou du terrain. */
export function referenceLat(bbox: readonly number[] | null | undefined): number {
  return bbox && bbox.length === 4 ? (bbox[1]! + bbox[3]!) / 2 : 46.5;
}
